import { PLAN_FEATURES, canonicalPlan } from "@/lib/plans/entitlements";
// Host-based tenant detection for the admin app.
//
// photohawk-web is reached three ways:
//   1. The app host itself — the apex (photohawk.com / photohawk-staging.com),
//      www, a reserved subdomain (admin/api/…), a *.pages.dev preview, or
//      localhost. Here the tenant is chosen by the /admin/workspaces/:alias
//      path (the internal-staff chooser) — there is no host tenant.
//   2. A tenant alias subdomain — {alias}.photohawk.com — the tenant is the
//      subdomain label.
//   3. A customer custom domain — anything else (e.g. photos.acme.com). The
//      tenant is resolved from the authenticated workspace list by matching the
//      host against each workspace's customDomain. That match is *guarded*: the
//      backend only returns customDomain for pro/enterprise tenants with active
//      DNS (see tenants.js getAvailableWorkspaces), so a non-qualifying domain
//      never resolves. isPlanAllowedForCustomDomain adds a defensive re-check.

export type HostTenant =
  | { type: "alias"; alias: string }
  | { type: "custom"; customDomain: string }
  | { type: "app" };

// Photohawk API base domains (prod + staging). A subdomain of one of these is a
// tenant alias unless it is a reserved/system label.
const APP_BASE_DOMAINS = ["photohawk.com", "photohawk-staging.com"];

// Subdomains of photohawk(-staging).com that are NOT tenant aliases.
const RESERVED_SUBDOMAINS = new Set([
  "www", "admin", "app", "api", "auth", "search", "workspaces", "checkout",
  "cdn", "assets", "static", "media", "img", "images", "mail", "email",
  "blog", "help", "docs", "support", "status", "go", "link", "dev", "staging",
  "upload",
]);

/**
 * Old Photohawk domains that the prod load balancer still sends to this app (ALB rule 11). They are
 * aliases of the marketing site, not tenant custom domains: the SSR server 301s them path-for-path to
 * https://photohawk.com (src/lib/seo/redirects.ts), and they're never resolved as a tenant.
 */
export const MARKETING_ALIAS_HOSTS: readonly string[] = [
  "photohawk.io",
  "www.photohawk.io",
  "thesearchfactory.com",
  "www.thesearchfactory.com",
];

/** The canonical marketing origin that the alias hosts redirect to. */
const MARKETING_APEX_ORIGIN = "https://photohawk.com";

export const isMarketingAliasHost = (host: string): boolean =>
  MARKETING_ALIAS_HOSTS.includes(host.toLowerCase().replace(/\.$/, "").split(":")[0]);

/** upload.photohawk.com (and staging): the standalone full-screen web uploader. */
export const isUploadHost = (
  host: string = typeof window !== "undefined" ? window.location.hostname : "",
): boolean => {
  const h = host.toLowerCase().replace(/\.$/, "");
  return APP_BASE_DOMAINS.some((base) => h === `upload.${base}`) || h === "upload.localhost";
};

// Plans allowed to use a custom domain (mirrors the backend guard): Pro and Max — the central
// entitlements map old names (enterprise → max) so this holds before and after the rename.
export const isPlanAllowedForCustomDomain = (plan?: string): boolean =>
  PLAN_FEATURES[canonicalPlan(plan)].customDomain;

/**
 * If the host is a `www.` variant of an app base domain, or an old marketing
 * domain (MARKETING_ALIAS_HOSTS), the URL to redirect to (the bare apex,
 * preserving path + query), else null. The client-side twin of the server's
 * 301s (src/lib/seo/redirects.ts), for a page that was served anyway.
 */
export const apexRedirectUrl = (
  hostname: string,
  pathname = "/",
  search = "",
): string | null => {
  const h = hostname.toLowerCase();
  for (const base of APP_BASE_DOMAINS) {
    if (h === `www.${base}`) return `https://${base}${pathname}${search}`;
  }
  if (isMarketingAliasHost(h)) return `${MARKETING_APEX_ORIGIN}${pathname}${search}`;
  return null;
};

/** Resolve the tenant implied by a hostname (defaults to the current host). */
export const detectHostTenant = (
  host: string = typeof window !== "undefined" ? window.location.hostname : "",
): HostTenant => {
  const h = host.toLowerCase().replace(/\.$/, "");

  // Local dev / preview hosts serve the app itself.
  if (!h || h === "localhost" || h.endsWith(".localhost")) return { type: "app" };
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(h)) return { type: "app" }; // raw IP
  if (h.endsWith(".pages.dev") || h.endsWith(".vercel.app")) {
    return { type: "app" };
  }
  // photohawk.io, thesearchfactory.com: old marketing domains, never a tenant's custom domain.
  if (isMarketingAliasHost(h)) return { type: "app" };

  for (const base of APP_BASE_DOMAINS) {
    if (h === base || h === `www.${base}`) return { type: "app" };
    if (h.endsWith(`.${base}`)) {
      const label = h.slice(0, h.length - base.length - 1);
      // Multi-level (api.*, admin.api.*, …) or a reserved label → app host.
      if (label.includes(".") || RESERVED_SUBDOMAINS.has(label)) return { type: "app" };
      return { type: "alias", alias: label };
    }
  }

  // Everything else is a customer custom domain (guarded at resolution time).
  return { type: "custom", customDomain: h };
};

/**
 * Whether a path is an admin *entry* point that should be scoped to the host
 * tenant. Only the admin lives in photohawk-web — the tenant's public site
 * (home, /pages/<slug>, /galleries/<slug>, …) is served by the public app, so
 * host detection must never touch those paths. Deep admin routes already carry
 * the alias (/admin/workspaces/:alias/…) and are left alone.
 */
export const isAdminEntryPath = (pathname: string): boolean => {
  const p = pathname.replace(/\/+$/, "") || "/";
  return p === "/admin" || p === "/admin/workspaces";
};
